'use client';

import { useDraftModeEnvironment, useIsPresentationTool } from 'next-sanity/hooks';
import { useEffect, useTransition } from 'react';
import { toast } from 'sonner';

type DraftModeToastProps = {
  disableDraftMode: () => Promise<void>;
};

export default function DraftModeToast({ disableDraftMode }: DraftModeToastProps) {
  const isPresentationTool = useIsPresentationTool();
  const env = useDraftModeEnvironment();
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    if (isPresentationTool === false) {
      /**
       * We delay the toast in case we're inside Presentation Tool
       */
      const toastId = toast('Draft Mode Enabled', {
        description: env === 'live' ? 'Content is live, refreshing automatically' : 'Refresh manually to see changes',
        duration: Infinity,
        action: {
          label: 'Disable',
          onClick: async () => {
            startTransition(() => {
              void disableDraftMode().then(() => {
                window.location.reload();
              });
            });
          },
        },
      });
      return () => {
        toast.dismiss(toastId);
      };
    }
  }, [disableDraftMode, env, isPresentationTool, pending]);

  useEffect(() => {
    if (pending) {
      const toastId = toast.loading('Disabling draft mode...');
      return () => {
        toast.dismiss(toastId);
      };
    }
  }, [pending]);

  return null;
}
